import { IMagnoliaContext } from '@/types/MagnoliaContext';

function getUrl(string: string): URL | false {
  try {
    const url = /^http(s?):\/\//i.test(string)
      ? string
      : 'http://valid-url.com' +
        (string.startsWith('/') ? string : '/' + string);

    return new URL(url);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    console.warn('requestUrl is not a valid URL');

    return false;
  }
}

function buildLangRegExp(language: string) {
  return new RegExp(`/${language}($|/)`);
}

/**
 * The requestUrl could be [http://<host>][/language][/spaRootNodePath][/nodePath][.html][?queryString][#fragment]
 * - All [xxx] could be missing
 * - [/language] must not equal [/spaRootNodePath]
 *
 * @param requestUrl could be relative or absolute
 * @param spaRootNodePath optional
 * @param languages optional
 * @returns
 */
export const getMagnoliaContext = (
  requestUrl: string,
  spaRootNodePath?: string,
  languages?: string[]
): IMagnoliaContext => {
  const url = getUrl(requestUrl);
  const magnoliaContext: IMagnoliaContext = {
    isMagnolia: false,
    isMagnoliaEdit: false,
    isMagnoliaPreview: false,
    version: undefined,
    nodePath: undefined,
    currentLanguage: undefined,
    searchParams: undefined,
    search: undefined,
  };

  if (url) {
    const pathname = url.pathname;
    const searchParams = new URLSearchParams(url.search);
    const mgnlPreview = searchParams.get('mgnlPreview');
    const mgnlPreviewAsVisitor = searchParams.get('mgnlPreviewAsVisitor');
    const mgnlVersion = searchParams.get('mgnlVersion');
    const preview = mgnlPreview == null ? mgnlPreviewAsVisitor : mgnlPreview;

    if (preview) {
      magnoliaContext.isMagnolia = true;

      if (preview === 'false') {
        magnoliaContext.isMagnoliaEdit = true;
      }
      if (preview === 'true') {
        magnoliaContext.isMagnoliaPreview = true;
      }
    }

    if (magnoliaContext.isMagnolia && !mgnlPreviewAsVisitor) {
      searchParams.set('variants', 'all');
    }

    if (mgnlVersion) {
      magnoliaContext.version = mgnlVersion;
      searchParams.set('version', mgnlVersion);
    } else {
      delete magnoliaContext.version;
    }

    let cleanPath = pathname;
    let hasLanguageInURL = false;
    if (languages && languages.length > 0) {
      magnoliaContext.currentLanguage = languages[0];
      hasLanguageInURL = languages.some(language => {
        if (buildLangRegExp(language).test(cleanPath)) {
          magnoliaContext.currentLanguage = language;
          return true;
        }
        return false;
      });
      searchParams.set('lang', magnoliaContext.currentLanguage);
    }

    if (spaRootNodePath) {
      if (hasLanguageInURL) {
        cleanPath = cleanPath.replace(
          buildLangRegExp(magnoliaContext.currentLanguage as string),
          '/'
        );
      }

      const endPathRegExp = new RegExp('(.html|/$)');
      const spaRootNodePathRegExp = new RegExp(`^${spaRootNodePath}($|/)`);
      cleanPath = cleanPath
        .replace(spaRootNodePathRegExp, '/')
        .replace(endPathRegExp, '');

      magnoliaContext.nodePath = spaRootNodePath + cleanPath;
    }

    magnoliaContext.searchParams = Object.fromEntries(searchParams);
    magnoliaContext.search = '?' + searchParams.toString();
  }

  return magnoliaContext;
};
